import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, CircularProgress, Drawer, IconButton, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Helper function to parse bilingual notification messages
 * If message is a JSON string with 'en' and 'es' keys, extract the message for the current language
 * Otherwise, return the message as-is (for backwards compatibility with old notifications)
 */
const parseNotificationMessage = (message: string, currentLanguage: string): string => {
  // Check if message starts with '{' to avoid unnecessary parsing
  if (!message.startsWith('{')) {
    return message;
  }

  const parsed = JSON.parse(message);
  if (parsed && typeof parsed === 'object' && ('en' in parsed || 'es' in parsed)) {
    // Return message in current language, fallback to Spanish if not found
    return parsed[currentLanguage] || parsed.es || parsed.en || message;
  }

  return message;
};

export interface NotificationDisplay {
  id: string;
  type: string;
  title?: string;
  message: string;
  isRead: boolean;
  timeAgo: string;
  link?: string;
  idAppointment?: string;
}

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  notifications: NotificationDisplay[];
  loadingMore?: boolean;
  hasMore?: boolean;
  isProcessing?: boolean;
  onClear: () => void | Promise<void>;
  onArchive: (id: string) => void | Promise<void>;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  onNotificationClick?: (notification: NotificationDisplay) => void;
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  open,
  onClose,
  notifications,
  loadingMore = false,
  hasMore = false,
  isProcessing = false,
  onClear,
  onArchive,
  onScroll,
  onNotificationClick,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const handleArchive = async (id: string): Promise<void> => {
    setArchivingId(id);
    try {
      await onArchive(id);
    } finally {
      setArchivingId(null);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 9999 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, bgcolor: '#f5f5f5' } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            bgcolor: theme.palette.primary.main,
            color: 'white',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: 40 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, textAlign: 'center' }}>
            {t('notifications.title')}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <LoadingButton
            variant="outlined"
            size="small"
            fullWidth
            onClick={onClear}
            loading={isProcessing && !archivingId}
            disabled={notifications.length === 0}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                bgcolor: theme.palette.primary.light + '10',
              },
            }}
          >
            {t('notifications.clearAll')}
          </LoadingButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }} onScroll={onScroll}>
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              onClick={() => {
                if (onNotificationClick) {
                  onNotificationClick(notification);
                }
              }}
              sx={{
                mb: 2,
                cursor: notification.link ? 'pointer' : 'default',
                bgcolor: notification.isRead ? 'white' : '#e3f2fd',
                border: notification.isRead ? '1px solid #e0e0e0' : `2px solid ${theme.palette.primary.light}`,
                '&:hover': { boxShadow: 3 },
                position: 'relative',
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleArchive(notification.id);
                }}
                disabled={archivingId === notification.id}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  color: 'text.disabled',
                  '&:hover': { color: theme.palette.error.main },
                }}
              >
                {archivingId === notification.id ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CloseIcon fontSize="small" />
                )}
              </IconButton>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {notification.title || t('notifications.defaultTitle')}
                    </Typography>
                    {!notification.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {parseNotificationMessage(notification.message, i18n.language)}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {notification.timeAgo}
                </Typography>
              </CardContent>
            </Card>
          ))}

          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loadingMore && notifications.length > 0 && hasMore === false && (
            <Typography variant="body2" color="text.disabled" align="center" sx={{ py: 2 }}>
              {t('notifications.noMore')}
            </Typography>
          )}

          {notifications.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t('notifications.empty')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
